import React from 'react'

import { Descriptions } from 'antd'
import { useLocation } from 'react-router-dom'

import { ClusterCluster } from '@clinic/client'
import { useCurOrg } from '~/hooks/state'
import { ReportBasic } from '~/pages/reports/shared/types'
import { ReportPVType, useReportPV } from '~/pages/reports/shared/utils/pv'
import { formatDateTime } from '~/utils/date-time'

import { type MetricMap } from './index'

export const SnapInfo: React.FC<{
  cluster?: ClusterCluster
  report: ReportBasic
  metrics: MetricMap
}> = ({ cluster, report, metrics }) => {
  const args = report.args
  return (
    <>
      <h1>1. Snap Info</h1>
      <BasicInfo cluster={cluster} report={report} />

      <table className='border-collapse'>
        <thead>
          <tr>
            <th className='text-left bg-[#fafafa] px-2 py-1 border-r border-solid border-[#f0f0f0]'></th>
            <th className='text-left bg-[#fafafa] px-2 py-1 border-r border-solid border-[#f0f0f0]'>
              Snap Time
            </th>
            <th className='text-left bg-[#fafafa] px-2 py-1 border-r border-solid border-[#f0f0f0]'>
              Total Connections
            </th>
            <th className='text-left bg-[#fafafa] px-2 py-1 border-r border-solid border-[#f0f0f0]'>
              Total Number of Online Nodes(TiDB/TiKV/PD/TiFlash)
            </th>
            <th className='text-left bg-[#fafafa] px-2 py-1 border-r border-solid border-[#f0f0f0]'>
              PD Leader
            </th>
            <th className='text-left bg-[#fafafa] px-2 py-1 border-r border-solid border-[#f0f0f0]'>
              DDL Owner
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className='px-2 py-1 border-r border-solid border-[#f0f0f0]'>
              Begin Snap
            </td>
            <td className='px-2 py-1 border-r border-solid border-[#f0f0f0]'>
              {formatDateTime(args.begin_ts)}
            </td>
            <td className='px-2 py-1 border-r border-solid border-[#f0f0f0]'>
              {metrics['Connnection_count:total']?.values.first.value}
            </td>
            <td className='px-2 py-1 border-r border-solid border-[#f0f0f0]'>
              {metrics['Component:total']?.values.first.value}
            </td>
            <td className='px-2 py-1 border-r border-solid border-[#f0f0f0]'>
              {metrics['PD_leader:by_instance']?.values.pd_leader_start.value}
            </td>
            <td className='px-2 py-1 border-r border-solid border-[#f0f0f0]'>
              {metrics['DDL_owner:by_instance']?.values.ddl_owner_start.value}
            </td>
          </tr>
          <tr>
            <td className='px-2 py-1 border-r border-solid border-[#f0f0f0]'>
              End Snap
            </td>
            <td className='px-2 py-1 border-r border-solid border-[#f0f0f0]'>
              {formatDateTime(args.end_ts)}
            </td>
            <td className='px-2 py-1 border-r border-solid border-[#f0f0f0]'>
              {metrics['Connnection_count:total']?.values.latest.value}
            </td>
            <td className='px-2 py-1 border-r border-solid border-[#f0f0f0]'>
              {metrics['Component:total']?.values.latest.value}
            </td>
            <td className='px-2 py-1 border-r border-solid border-[#f0f0f0]'>
              {metrics['PD_leader:by_instance']?.values.pd_leader_end.value}
            </td>
            <td className='px-2 py-1 border-r border-solid border-[#f0f0f0]'>
              {metrics['DDL_owner:by_instance']?.values.ddl_owner_end.value}
            </td>
          </tr>
          <tr>
            <td className='px-2 py-1 border-r border-solid border-[#f0f0f0]'>
              Elapsed
            </td>
            <td className='px-2 py-1 border-r border-solid border-[#f0f0f0]'>
              {parseInt(`${(args.end_ts - args.begin_ts) / 60}`)} mins
            </td>
            <td
              colSpan={4}
              className='px-2 py-1 border-r border-solid border-[#f0f0f0]'
            ></td>
          </tr>
        </tbody>
      </table>
    </>
  )
}

const BasicInfo: React.FC<{
  cluster?: ClusterCluster
  report?: {
    creator: string
    createdAt: number
  }
}> = ({ cluster, report }) => {
  const { curOrg } = useCurOrg()
  const location = useLocation()
  const { pv } = useReportPV(ReportPVType.SystemCheck, location.pathname)

  return (
    <Descriptions column={2}>
      <Descriptions.Item label='Tenant'>{curOrg?.name}</Descriptions.Item>
      <Descriptions.Item label='Cluster'>{cluster?.name}</Descriptions.Item>
      <Descriptions.Item label='Creator'>{report?.creator}</Descriptions.Item>
      <Descriptions.Item label='Created At'>
        {formatDateTime(report?.createdAt as any)}
      </Descriptions.Item>
      <Descriptions.Item label='View Count'>{pv}</Descriptions.Item>
    </Descriptions>
  )
}
